import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X } from 'lucide-react';
import { useOnlineOrders } from '../../api/menu.api';
import { usePermission } from '../../hooks/usePermissions';
import { useI18n } from '../../i18n/I18nProvider';
import { formatLbp } from '../../utils/format';
import type { OnlineOrder } from '../../types';

// Same inbox query and polling cadence. A timestamp watermark also prevents
// older rows entering the first page from masquerading as new arrivals.
export function MenuOrderArrival() {
  const enabled = usePermission('menu.orders.view');
  const { data } = useOnlineOrders('PENDING_CONFIRMATION', enabled);
  const baseline = useRef<{ time: string; ids: Set<string> } | null>(null);
  const [arrival, setArrival] = useState<{ order: OnlineOrder; count: number } | null>(null);
  const { t, locale, format } = useI18n();
  const navigate = useNavigate();

  useEffect(() => {
    if (!enabled) { baseline.current = null; setArrival(null); return; }
    if (!data || !Array.isArray(data.orders)) return;
    const newest = data.orders.reduce((time, order) => order.submittedAt > time ? order.submittedAt : time, baseline.current?.time ?? '');
    if (baseline.current) {
      const previous = baseline.current;
      const fresh = data.orders.filter(order => order.submittedAt > previous.time || (order.submittedAt === previous.time && !previous.ids.has(order.id)));
      if (fresh.length) setArrival(current => ({ order: fresh[0]!, count: (current?.count ?? 0) + fresh.length }));
    }
    const ids = new Set(baseline.current?.time === newest ? baseline.current.ids : []);
    for (const order of data.orders) if (order.submittedAt === newest) ids.add(order.id);
    baseline.current = { time: newest, ids };
  }, [data, enabled]);

  useEffect(() => {
    if (!arrival) return;
    const timer = window.setTimeout(() => setArrival(null), 10_000);
    return () => window.clearTimeout(timer);
  }, [arrival]);

  return <>
    {arrival && enabled ? <aside role="status" aria-live="polite" data-testid="menu-order-arrival" className="fixed bottom-24 start-4 z-40 w-[min(24rem,calc(100vw-2rem))] rounded-card border border-antique-gold bg-surface p-4 shadow-xl till:bottom-6 motion-safe:animate-[fade-in_180ms_ease-out]">
      <div className="flex items-center gap-2 font-semibold text-butcher-green"><Bell className="h-5 w-5" aria-hidden="true" />{t.menu.orders.newArrival}<span className="numeric">{arrival.count > 1 ? `(${arrival.count})` : ''}</span><button type="button" className="ms-auto flex h-9 w-9 items-center justify-center rounded-full" aria-label={t.common.close} onClick={() => setArrival(null)}><X className="h-4 w-4" /></button></div>
      <p className="numeric text-sm" dir="ltr">{arrival.order.orderNumber}</p>
      <p className="mt-1 truncate text-sm">{arrival.order.customerName}</p>
      <p className="numeric text-sm text-ink-muted">{format(t.menu.orders.items, { count: arrival.order.itemCount })} · {formatLbp(arrival.order.totalLbp, locale)}</p>
      <button type="button" className="btn-primary mt-3" data-testid="menu-order-arrival-view" onClick={() => { setArrival(null); navigate(`/menu-orders?mo=${arrival.order.id}`); }}>{t.menu.orders.view}</button>
    </aside> : null}
  </>;
}
